import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '@/i18n';
import { dataService } from '@/services/dataService';
import { runCommand } from '@/services/commandRouter';

const EXAMPLES = ['whatNeedsAttention', 'showAtRisk', 'showPendingApprovals', 'showExpiringContracts'];

/**
 * "Ask Ugarits AI" — no LLM is connected (see aiProviders.config.ts, every
 * provider is `configured: false`), so this never fabricates an answer.
 * A small set of SAFE local queries (matching demo data the app already
 * has) resolve to a real, clearly-labeled "Local Preview Result" instead
 * of a canned AI-sounding sentence — everything else stays honestly
 * disabled. This is the distinction the brief asks for at #49.
 */
export function AskAiDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [result, setResult] = useState<string | null>(null);

  if (!open) return null;

  async function runLocalQuery(q: string) {
    const lower = q.toLowerCase();
    if (lower.includes('risk')) {
      const creators = await dataService.listCreators();
      const atRisk = creators.filter((c) => c.riskLevel === 'high');
      setResult(atRisk.length ? `${atRisk.length} creator(s) at risk: ${atRisk.map((c) => c.fullName).join(', ')}` : 'No creators currently at risk.');
      return;
    }
    if (lower.includes('approval')) {
      const approvals = await dataService.listApprovals();
      const pending = approvals.filter((a) => a.status === 'pending');
      setResult(pending.length ? `${pending.length} approval(s) pending.` : 'No approvals pending.');
      return;
    }
    if (lower.includes('contract')) {
      const contracts = await dataService.listContracts();
      const expiring = contracts.filter((c) => c.status === 'expiring_soon');
      setResult(expiring.length ? `${expiring.length} contract(s) expiring soon.` : 'No contracts flagged as expiring in demo data.');
      return;
    }
    if (lower.includes('attention')) {
      navigate('/');
      onClose();
      return;
    }
    const live = await runCommand(q);

    if (live.kind === 'ai_generated') {
      setResult(live.text ?? null);
      return;
    }

    if (live.kind === 'ai_error') {
      setResult(live.error ?? 'AI request failed');
      return;
    }

    if (live.kind === 'not_connected') {
      setResult(t('aiCommand.notConnectedBody'));
      return;
    }

    setResult(null);
  }

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <div className="drawer-panel" style={{ width: 420 }}>
        <div style={{ fontWeight: 700, marginBottom: 4 }}>{t('askAi.title')}</div>

        <input
          className="text-input"
          placeholder={t('askAi.placeholder')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && runLocalQuery(query)}
          style={{ marginBottom: 10 }}
        />
        <button className="btn-primary" onClick={() => runLocalQuery(query)} style={{ marginBottom: 16 }}>{t('askAi.run')}</button>

        {result && (
          <div className="card" style={{ padding: 12, marginBottom: 16 }}>
            <div style={{ fontSize: 13.5 }}>{result}</div>
          </div>
        )}

        <div style={{ fontSize: 11, textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: 8 }}>{t('askAi.examples')}</div>
        {EXAMPLES.map((key) => (
          <div key={key} className="popover-item" onClick={() => { setQuery(t(`askAi.example.${key}`)); runLocalQuery(t(`askAi.example.${key}`)); }}>
            {t(`askAi.example.${key}`)}
          </div>
        ))}
      </div>
    </>
  );
}
